import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Modal, Spinner } from 'react-bootstrap'
import Header from '../components/layout/Header'
import Footer from '../components/layout/Footer'
import axios from 'axios'
import BannerImg from '../assets/images/banners/Banner1.webp'

import {
    Calendar3,
    FileEarmarkArrowDown,
    TelephoneFill,
    Whatsapp,
    Download,
    XLg,
    EnvelopeFill,
    Facebook,
    Instagram,
    Tiktok,
    Linkedin,
} from 'react-bootstrap-icons'

// ─── LOCAL REPORT CARD SUBCOMPONENT ─────────────────────────────────────────
function ReportCard({ reportItem, index, onSelect }) {
    const [hovered, setHovered] = useState(false)

    return (
        <div
            style={{
                ...detailStyles.brochureCard,
                ...(hovered ? detailStyles.brochureCardHover : {}),
            }}
            className="mb-3 brochure-card-animate"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => onSelect(reportItem)}
        >
            <div style={detailStyles.brochureIndex}>{String(index + 1).padStart(2, '0')}</div>

            <div style={detailStyles.brochureIconWrap}>
                <FileEarmarkArrowDown className="fs-ss-24" style={{ color: '#b78b4c', fontSize: '24px' }} />
            </div>

            <div style={detailStyles.brochureInfo}>
                <div style={detailStyles.brochureName} title={reportItem.title}>
                    {reportItem.title || 'Untitled Document Container'}
                </div>
                {reportItem.description && (
                    <div className="py-2" style={detailStyles.brochureDescription}>
                        {reportItem.description}
                    </div>
                )}
                <div style={detailStyles.brochureMeta}>
                    {reportItem.createdAt && (
                        <>
                            <span>
                                <Calendar3 className="me-2 mb-1" size={12} />
                                {new Date(reportItem.createdAt).toLocaleDateString('en-US', {
                                    month: 'long',
                                    year: 'numeric',
                                })}
                            </span>
                        </>
                    )}
                </div>
            </div>

            <button
                className="border-0"
                style={{
                    ...detailStyles.downloadBtn,
                    ...(hovered ? detailStyles.downloadBtnHover : {}),
                }}
                aria-label={`Open media files inside ${reportItem.title}`}
            >
                <span>View Report</span>
            </button>
        </div>
    )
}

// ─── MAIN REPORTS COMPONENT ──────────────────────────────────────────────────
const Reports = () => {
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const [reports, setReports] = useState([])
    const [contentLoading, setContentLoading] = useState(true)
    const [selectedReport, setSelectedReport] = useState(null)
    const [downloadingFiles, setDownloadingFiles] = useState({})

    const handleDownload = async (filename, originalName) => {
        setDownloadingFiles((prev) => ({ ...prev, [filename]: true }))
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/reports/file/${filename}`, {
                responseType: 'blob',
            })
            const url = window.URL.createObjectURL(res.data)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', originalName || filename)
            document.body.appendChild(link)
            link.click()
            link.remove()
            setTimeout(() => window.URL.revokeObjectURL(url), 100)
        } catch (err) {
            console.error('Error downloading asset structure:', err)
        } finally {
            setDownloadingFiles((prev) => ({ ...prev, [filename]: false }))
        }
    }

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_API_URL}/reports/website`)
            .then((res) => {
                if (res.data && res.data.reports && res.data.reports.length > 0) {
                    setReports(res.data.reports)
                } else {
                    setReports([])
                }
            })
            .catch((err) => {
                console.error('Database connection processing failed:', err)
            })
            .finally(() => {
                setContentLoading(false)
            })
    }, [])

    return (
        <div style={detailStyles.page}>
            <style dangerouslySetInnerHTML={{ __html: detailGlobalCSS }} />

            <Helmet>
                <title> Dubai Real Estate Market Reports | 800 Homes </title>
                <meta
                    name="description"
                    content="Explore the latest Dubai real estate market reports, trends and insights. Download monthly property reports to make informed investment decisions with 800 Homes."
                />
            </Helmet>

            <Header />

            {/* Original Banner Section */}
            <section
                className="container-fluid common-banner shadow-800homes"
                style={{ backgroundImage: `url(${BannerImg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            >
                <div className="row justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <div className="col-12 col-md-10">
                        <h1 className="lh-sm fw-500 fs-ss-blog text-white text-center pb-5 pb-md-0">
                            {' '}
                            Real Estate Reports & Insights{' '}
                        </h1>
                    </div>
                </div>
            </section>

            {/* Layout Grid container frame structure */}
            <div className="bg-page py-5" style={detailStyles.mainWrap}>
                <div className="container-fluid">
                    <div
                        className="d-flex flex-column flex-md-row justify-content-center align-items-start detail-main-inner"
                        style={detailStyles.mainInner}
                    >
                        {/* LEFT GRID COLUMN - MAIN DYNAMIC FILES LIST */}
                        <div style={detailColStyles.expandedLeftCol} className="left-content-flexframe">
                            {contentLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" style={{ color: '#b78b4c' }} />
                                    <p className="text-muted mt-3">Fetching document indexing tables...</p>
                                </div>
                            ) : reports.length > 0 ? (
                                <div style={detailStyles.brochureList} className="brochure-card-container">
                                    {reports.map((fileItem, idx) => (
                                        <ReportCard
                                            key={fileItem._id || idx}
                                            reportItem={fileItem}
                                            index={idx}
                                            onSelect={setSelectedReport}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div
                                    className="mb-5 p-4 text-center rounded"
                                    style={{ border: '1px dashed rgba(255,255,255,0.2)' }}
                                >
                                    <p style={{ fontSize: '18px', color: 'white', margin: 0 }}>
                                        No reports available yet.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* RIGHT GRID SIDEBAR PANEL */}
                        <aside style={detailStyles.sidebar} className="detail-sidebar">
                            <div style={detailStyles.sidebarStickyWrapper}>
                                <div style={detailStyles.sideCard}>
                                    <div style={detailStyles.agentRow}>
                                        <div>
                                            <div className="fs-ss-26 fw-500 lh-sm text-white">Get in touch</div>
                                        </div>
                                    </div>

                                    <p style={detailStyles.sideNote}>
                                        Have questions about our reports or the Dubai market? Speak to our team for
                                        expert analysis, market updates and investment advice on Dubai real estate.
                                    </p>

                                    <a href="tel:80046637" style={{ textDecoration: 'none' }}>
                                        <div
                                            className="text-decoration-none px-4 py-ss-2 text-center text-white bg-ss-primary bg-hover-ss-primary rounded-1 mb-3 d-flex align-items-center justify-content-center"
                                            style={{ minHeight: '44px' }}
                                        >
                                            <TelephoneFill className="text-white me-2 mb-1" />
                                            <span> Call Now </span>
                                        </div>
                                    </a>

                                    <a
                                        href="https://api.whatsapp.com/send/?phone=971585746637"
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <button style={detailStyles.ctaWhatsApp}>
                                            <Whatsapp className="me-2" style={{ color: '#25D366' }} />
                                            WhatsApp
                                        </button>
                                    </a>
                                </div>

                                <div className="mb-5 mb-md-0" style={detailStyles.imageDownloadAllCard}>
                                    <div className="">
                                        <div className="fs-ss-18 text-white py-3">Follow Us</div>
                                        <div className="text-ss-secondary fs-ss-22 text-ss-primary">
                                            <a
                                                href="mailto:inquiry@800homes.ae"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-decoration-none text-ss-primary text-hover-ss-primary"
                                            >
                                                {' '}
                                                <EnvelopeFill className="me-3 mb-4" />{' '}
                                            </a>
                                            <a
                                                href="https://www.facebook.com/800Homes"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-decoration-none text-ss-primary text-hover-ss-primary"
                                            >
                                                {' '}
                                                <Facebook className="me-3 mb-4" />{' '}
                                            </a>
                                            <a
                                                href="https://www.instagram.com/800homesae/"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-decoration-none text-ss-primary text-hover-ss-primary"
                                            >
                                                {' '}
                                                <Instagram className="me-3 mb-4" />{' '}
                                            </a>
                                            <a
                                                href="https://www.tiktok.com/@800homes"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-decoration-none text-ss-primary text-hover-ss-primary"
                                            >
                                                {' '}
                                                <Tiktok className="me-3 mb-4" />{' '}
                                            </a>
                                            <a
                                                href="https://www.linkedin.com/company/800-homes-real-estate-brokerage-firm/"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-decoration-none text-ss-primary text-hover-ss-primary"
                                            >
                                                {' '}
                                                <Linkedin className="me-3 mb-4" />{' '}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </div>

            {/* DYNAMIC MODAL ATTACHMENTS BOX */}
            <Modal show={!!selectedReport} onHide={() => setSelectedReport(null)} centered size="md">
                <div
                    className="bg-page border border-white border-1 rounded-1 overflow-hidden"
                    style={{ background: '#111' }}
                >
                    <div className="d-flex justify-content-between align-items-center p-4 border-bottom border-white border-1">
                        <div className="text-white fs-ss-18 fw-500">{selectedReport?.title}</div>
                        <button
                            onClick={() => setSelectedReport(null)}
                            className="border-0 bg-transparent text-grey p-0"
                            style={{ color: '#fff' }}
                        >
                            <XLg size={16} />
                        </button>
                    </div>
                    <div className="p-4" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                        {selectedReport?.files && selectedReport.files.length > 0 ? (
                            selectedReport.files.map((file, i) => (
                                <div key={i} className="d-flex justify-content-between align-items-center py-3">
                                    <span
                                        className="text-grey fs-ss-15 me-3"
                                        style={{ wordBreak: 'break-all', color: '#ddd' }}
                                    >
                                        {file.original_name || file.filename}
                                    </span>
                                    <button
                                        onClick={() => handleDownload(file.filename, file.original_name)}
                                        disabled={!!downloadingFiles[file.filename]}
                                        className="border-0 flex-shrink-0 px-3 py-2 text-white btn btn-primary rounded-5 d-inline-flex align-items-center fs-ss-14"
                                        style={{ backgroundColor: '#b78b4c', borderColor: '#b78b4c' }}
                                    >
                                        <Download className="me-2" />
                                        {downloadingFiles[file.filename] ? 'Downloading...' : 'Download'}
                                    </button>
                                </div>
                            ))
                        ) : (
                            <p className="text-muted text-center my-3">
                                No separate attached files found inside this report category.
                            </p>
                        )}
                    </div>
                </div>
            </Modal>

            <Footer />
        </div>
    )
}

// ─── STYLING & CORE TOKEN DICTIONARIES ───────────────────────────────────────
const detailColStyles = {
    expandedLeftCol: {
        flex: 1,
        width: '100%',
        minWidth: '0',
    },
}

const detailGlobalCSS = `
    .project-details-content,
    .project-details-content *,
    .project-details-content span,
    .project-details-content p,
    .project-details-content div {
        color: rgba(255, 255, 255, 0.8) !important;
    }

    .pdf-title-link {
        cursor: pointer;
        transition: opacity 0.2s ease, text-decoration 0.2s ease;
    }

    .pdf-title-link:hover {
        opacity: 0.8;
        text-decoration: underline !important;
    }

    .brochure-card-animate {
        animation: fadeSlideUp 0.35s ease both;
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    @keyframes fadeSlideUp {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }

    @media (max-width: 960px) {
        .detail-main-inner { 
            flex-direction: column !important; 
            gap: 40px !important; 
        }
        .detail-sidebar { 
            width: 100% !important; 
            position: static !important; 
        }
    }

    @media (max-width: 768px) {
        .detail-stats-row { 
            grid-template-columns: repeat(2, 1fr) !important; 
            gap: 12px !important; 
        }
        .detail-main-inner {
            gap: 32px !important;
        }
    }

    @media (max-width: 600px) {
        .detail-stats-row { 
            grid-template-columns: repeat(2, 1fr) !important; 
            gap: 10px !important; 
        }
        .detail-sidebar {
            gap: 16px !important;
        }
        .brochure-card-container {
            gap: 10px !important;
        }
        .brochure-card-animate {
            flex-wrap: wrap !important;
        }
        .brochure-card-animate button {
            flex-basis: 100% !important;
            width: 100% !important;
        }
        .detail-main-inner {
            gap: 24px !important;
        }
    }

    @media (max-width: 480px) {
        .detail-stats-row { 
            grid-template-columns: repeat(2, 1fr) !important; 
            gap: 8px !important; 
        }
    }
`

const detailStyles = {
    page: {
        minHeight: '100vh',
        color: '#ffffff',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
    },
    mainWrap: {
        width: '100%',
        padding: 'clamp(16px, 4vw, 48px) clamp(12px, 3vw, 32px)',
    },
    mainInner: {
        maxWidth: '1200px',
        margin: '0 auto',
        gap: 'clamp(32px, 5vw, 64px)',
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
    },
    sectionTitle: {
        fontSize: 'clamp(18px, 4vw, 24px)',
        fontWeight: 500,
        color: '#ffffff',
        margin: '0 0 16px 0',
    },
    brochureCountBadge: {
        fontSize: '12px',
        background: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '3px 8px',
        borderRadius: '12px',
        color: '#ffffff',
    },
    brochureList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(8px, 2vw, 12px)',
    },
    brochureCard: {
        display: 'flex',
        alignItems: 'center',
        padding: 'clamp(19px, 3vw, 16px) clamp(12px, 3vw, 20px)',
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '6px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        gap: 'clamp(6px, 1.5vw, 12px)',
        flexWrap: 'wrap',
        minHeight: '0',
        cursor: 'pointer',
    },
    brochureCardHover: {
        background: 'rgba(255, 255, 255, 0.05)',
        transform: 'translateY(-1px)',
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.25)',
    },
    brochureIndex: {
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.4)',
        marginRight: 'clamp(8px, 2vw, 16px)',
        display: 'none',
    },
    brochureIconWrap: {
        marginRight: 'clamp(8px, 2vw, 16px)',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
    },
    brochureInfo: {
        flex: 1,
        minWidth: 0,
        paddingRight: 'clamp(8px, 2vw, 16px)',
    },
    brochureName: {
        fontSize: 'clamp(14px, 2.5vw, 18px)',
        fontWeight: 500,
        color: '#ffffff',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    brochureMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.5)',
        marginTop: '4px',
    },
    metaDot: {
        opacity: 0.5,
    },
    downloadBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        marginTop: '7px',
        justifyContent: 'center',
        padding: 'clamp(6px, 2vw, 8px) clamp(10px, 2vw, 14px)',
        fontSize: 'clamp(12px, 2vw, 13px)',
        fontWeight: 500,
        border: '1px solid rgba(255, 255, 255, 0.3)',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '2px',
        color: '#ffffff',
        cursor: 'pointer',
        transition: 'all 0.2s',
        outline: 'none',
        whiteSpace: 'nowrap',
    },
    downloadBtnHover: {
        borderColor: '#ffffff',
        color: '#ffffff',
    },
    downloadBtnLoading: {
        background: 'transparent',
        color: 'rgba(255, 255, 255, 0.4)',
        borderColor: 'rgba(255, 255, 255, 0.15)',
        cursor: 'not-allowed',
    },
    sidebar: {
        width: '320px',
        flexShrink: 0,
    },
    sidebarStickyWrapper: {
        position: 'sticky',
        top: '100px',
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(16px, 3vw, 20px)',
    },
    sideCard: {
        background: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.2)',
        borderRadius: '8px',
        padding: 'clamp(16px, 4vw, 28px)',
    },
    agentRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        marginBottom: '20px',
    },
    sideNote: {
        fontSize: 'clamp(13px, 2vw, 13px)',
        lineHeight: '1.6',
        color: 'rgba(255, 255, 255, 0.75)',
        margin: '0 0 24px 0',
    },
    ctaWhatsApp: {
        width: '100%',
        padding: '11px',
        background: 'transparent',
        color: '#ffffff',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '4px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
    },
    imageDownloadAllCard: {
        background: 'transparent',
        borderRadius: '8px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        padding: 'clamp(13px, 4vw, 12px) clamp(12px, 4vw, 24px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
        gap: 'clamp(12px, 3vw, 16px)',
        flexWrap: 'wrap',
    },
    imageDownloadTextContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    imageDownloadTitle: {
        color: '#ffffff',
        fontSize: 'clamp(15px, 2.5vw, 15px)',
        fontWeight: '500',
        fontFamily: "'DM Sans', sans-serif",
    },
    brochureDescription: {
        fontSize: 'clamp(14px, 2.5vw, 15px)',
        lineHeight: '1.4',
        marginTop: '4px',
        color: 'rgba(255, 255, 255, 0.8)',
        margin: 0,
        fontWeight: 300,
    },
    imageDownloadSubTitle: {
        color: 'rgba(255, 255, 255, 0.5)',
        fontSize: 'clamp(13px, 2vw, 13px)',
        fontWeight: '400',
        fontFamily: "'DM Sans', sans-serif",
    },
    imageDownloadCircleBtn: {
        width: '35px',
        height: '35px',
        borderRadius: '50%',
        background: '#b78b4c',
        color: '#ffffff',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'transform 0.15s ease, background-color 0.2s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    },
    imageDownloadCircleBtnHover: {
        background: '#a57a3f',
        transform: 'scale(1.04)',
    },
}

export default Reports
